import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/agenda";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || "").join(" ");
  return "Algo no ha ido bien. Prueba otra vez.";
};

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen flex items-center justify-center warm-canvas px-5 py-10">
    <div className="w-full max-w-sm">
      <div className="flex justify-center mb-6"><Logo /></div>
      <div className="bg-white rounded-3xl border border-[#EAE5DF] p-7 shadow-[0_20px_50px_-24px_rgba(46,42,143,0.25)]">
        <h1 className="font-heading text-2xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1 mb-5">{subtitle}</p>}
        {children}
      </div>
    </div>
  </div>
);

const GoogleButton = () => {
  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={googleLogin} data-testid="google-login-button"
      className="w-full flex items-center justify-center gap-2 rounded-full border border-slate-200 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
      Continuar con Google
    </button>
  );
};

export const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password); navigate("/app"); }
    catch (err) { toast.error(errText(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Entra en tu agenda" subtitle="Accede con tu cuenta para continuar.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label htmlFor="l-email">Email</Label><Input id="l-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" /></div>
        <div className="space-y-1.5"><Label htmlFor="l-pass">Contraseña</Label><Input id="l-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" /></div>
        <div className="text-right"><Link to="/forgot-password" className="text-xs text-[#275FBE] hover:underline">¿Olvidaste la contraseña?</Link></div>
        <Button type="submit" disabled={loading} className="w-full rounded-full text-white" style={{ backgroundColor: "#275FBE" }} data-testid="login-submit">{loading ? "Entrando..." : "Entrar"}</Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-slate-400"><span className="flex-1 h-px bg-slate-100" />o<span className="flex-1 h-px bg-slate-100" /></div>
      <GoogleButton />
      <p className="text-sm text-slate-500 mt-5 text-center">¿No tienes cuenta? <Link to="/register" className="text-[#275FBE] font-medium hover:underline">Crea una</Link></p>
    </Shell>
  );
};

export const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await register(email, password, name); navigate("/app"); }
    catch (err) { toast.error(errText(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Crea tu cuenta" subtitle="Empieza a organizar tu agenda hoy mismo.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label htmlFor="r-name">Nombre</Label><Input id="r-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" /></div>
        <div className="space-y-1.5"><Label htmlFor="r-email">Email</Label><Input id="r-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" /></div>
        <div className="space-y-1.5"><Label htmlFor="r-pass">Contraseña</Label><Input id="r-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password" /><p className="text-xs text-slate-400">Mínimo 6 caracteres.</p></div>
        <Button type="submit" disabled={loading} className="w-full rounded-full text-white" style={{ backgroundColor: "#275FBE" }} data-testid="register-submit">{loading ? "Creando..." : "Crear cuenta"}</Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-slate-400"><span className="flex-1 h-px bg-slate-100" />o<span className="flex-1 h-px bg-slate-100" /></div>
      <GoogleButton />
      <p className="text-sm text-slate-500 mt-5 text-center">¿Ya tienes cuenta? <Link to="/login" className="text-[#275FBE] font-medium hover:underline">Entra</Link></p>
    </Shell>
  );
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await authApi.forgot(email); setSent(true); }
    catch { setSent(true); } finally { setLoading(false); }
  };
  return (
    <Shell title="Recupera tu contraseña" subtitle={sent ? null : "Te enviaremos un enlace a tu email."}>
      {sent ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Si ese email está registrado, te hemos enviado un enlace para restablecer la contraseña. Revisa tu bandeja de entrada.</p>
          <Link to="/login" className="text-sm text-[#275FBE] font-medium hover:underline">Volver a entrar</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="f-email">Email</Label><Input id="f-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" /></div>
          <Button type="submit" disabled={loading} className="w-full rounded-full text-white" style={{ backgroundColor: "#275FBE" }} data-testid="forgot-submit">{loading ? "Enviando..." : "Enviar enlace"}</Button>
          <Link to="/login" className="block text-sm text-slate-500 hover:underline text-center">Volver a entrar</Link>
        </form>
      )}
    </Shell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await authApi.reset(token, password); toast.success("Contraseña cambiada. Ya puedes entrar."); navigate("/login"); }
    catch (err) { toast.error(errText(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Nueva contraseña" subtitle="Elige una contraseña nueva para tu cuenta.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label htmlFor="rp-pass">Contraseña</Label><Input id="rp-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password" /><p className="text-xs text-slate-400">Mínimo 6 caracteres.</p></div>
        <Button type="submit" disabled={loading || !token} className="w-full rounded-full text-white" style={{ backgroundColor: "#275FBE" }} data-testid="reset-submit">{loading ? "Guardando..." : "Guardar contraseña"}</Button>
        <Link to="/login" className="block text-sm text-slate-500 hover:underline text-center">Volver a entrar</Link>
      </form>
    </Shell>
  );
};
